import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import uuid from 'uuid/v4';

import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { ADNode } from '@atlaskit/editor-common/validator';
import type { EditorActions } from '@atlaskit/editor-core';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type ActionType as FlagActionType } from '@atlaskit/flag/types';
import MentionIcon from '@atlaskit/icon/core/mention';
import Avatar from '@atlaskit/avatar/Avatar';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { NotificationAction } from './graphql/__types__/CreateContentMentionNotificationAction.ts';
import { useUsersContentPermissions } from './hooks/useUsersContentPermissions.ts';
import type { MentionNotificationType } from './hooks/useMentionNotificationMutation.ts';
import { useMentionNotificationMutation } from './hooks/useMentionNotificationMutation.ts';
import type { UpdatedTaskItem } from './hooks/useCreateInlineTaskNotificationMutation.ts';
import { useCreateInlineTaskNotificationMutation } from './hooks/useCreateInlineTaskNotificationMutation.ts';
import {
	ConfluencePrincipalType,
	ContentRole,
} from './graphql/__types__/UpdateContentPermissionsMutation.ts';
import { Operation } from './graphql/__types__/CreateInlineTaskNotificationMutation.ts';
import { mentionsMessages } from './mentionsi18n.ts';
import { useCanEditPageRestrictions } from './hooks/useCanEditPageRestrictions.ts';
import type { MentionType } from './hooks/useMentionsNotifications.tsx';
import { useMentionsNotifications } from './hooks/useMentionsNotifications.tsx';
import { getElapsedTime } from './mentionsUtils.ts';

type MentionsNotificationsComponentProps = {
	flags: FlagsStateContainer;
	contentId: string;
	isLivePage: boolean;
	isMentionsNotificationsEnabled: boolean;
	editorActions?: EditorActions;
};

type MentionsNotificationsProps = MentionsNotificationsComponentProps & {
	children: React.ReactNode;
};

type ShowCustomFlagProps = {
	title: string;
	description: string;
	userPhoto?: string;
	handleOnClose?: () => void;
	autoDismiss?: boolean;
	id?: string;
};

type ShowCustomFlagWithActionsProps = {
	title: string;
	description: string;
	userPhoto?: string;
	actions?: FlagActionType[];
	handleOnClose?: () => void;
	autoDismiss?: boolean;
	id?: string;
	actionContent?: string;
	actionOnClick?: () => void;
};

type DisplayNoPermissionsFlagParams = {
	displayNames: Set<string | undefined>;
	flagFields: ShowCustomFlagWithActionsProps;
	displayName: string;
	somebodyElse: string;
	flagId: string;
	type?: string;
	hasAction?: boolean;
	userPhoto?: string;
	restrictedLevel?: string;
};

const getDisplayNames = (mentions: MentionType[]) => {
	const displayNames = new Set(mentions.map((mention) => mention.displayName));
	const iterator = displayNames.values();
	const displayName = iterator.next().value ?? '';
	const somebodyElse = iterator.next().value ?? '';
	return { displayNames, displayName, somebodyElse };
};

export const MentionsNotificationsComponent = ({
	flags,
	contentId,
	isLivePage,
	isMentionsNotificationsEnabled,
	editorActions,
}: MentionsNotificationsComponentProps) => {
	const [
		{
			addedMentions,
			deletedMentions,
			addedTasks,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
			deletedTasks,
			mentionsAdded,
			tasksAdded,
		},
		{
			setAddedMentions,
			setDeletedMentions,
			setUnrestrictedMentions,
			setParentSpaceRestrictedMentions,
			setPageRestrictedMentions,
			setUnrestrictedTasks,
			setPageRestrictedTasks,
			setParentSpaceRestrictedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId: currentUserId } = useSessionData();
	//General Mention Flag IDs
	const [currentAddedMentionFlagId, setCurrentAddedMentionFlagId] = useState<string>('');
	const [
		currentSpaceOrParentRestrictionMentionFlagId,
		setCurrentSpaceOrParentRestrictionMentionFlagId,
	] = useState('');
	const [currentPageRestrictionMentionFlagId, setCurrentPageRestrictionMentionFlagId] =
		useState('');
	//Task Mentions Flag IDs
	const [currentAddedTaskFlagId, setCurrentAddedTaskFlagId] = useState('');
	const [currentPageRestrictionTaskFlagId, setCurrentPageRestrictionTaskFlagId] = useState('');
	const [currentSpaceOrParentRestrictionTaskFlagId, setCurrentSpaceOrParentRestrictionTaskFlagId] =
		useState('');
	//General Mention Timeouts
	const addedUnrestrictedMentionFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const pageRestrictedMentionFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const spaceParentRestrictedMentionFlagTimeout = useRef<
		ReturnType<typeof setTimeout> | undefined
	>();
	//Task Mention Timeouts
	const addedUnrestrictedTaskFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const pageRestrictedTaskFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const spaceParentRestrictedTaskFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

	const [error, setError] = useState<Error | null>(null);

	const mentionsToDisplay = useMemo(
		() => [...addedMentions, ...addedTasks],
		[addedMentions, addedTasks],
	);

	const {
		updatedUsersContentPermissionsError,
		updatedUsersContentPermissionsLoading,
		updateUsersContentPermissionMutation,
	} = useUsersContentPermissions({
		contentId,
		isUpdatedMentionsInLivePagesEnabled: isMentionsNotificationsEnabled,
		isLivePage,
		currentUserId,
		mentionsToDisplay,
		source: 'mentionNoAccessFlag',
	});

	const canEditPageRestrictions = useCanEditPageRestrictions();

	useEffect(() => {
		const getDuplicateMentions = async (mentions: MentionType[]) => {
			const duplicateMentions = new Set();
			if (mentions.length > 0) {
				const mentionCounts = new Map();
				const editorContent: ADNode = await editorActions?.getValue();
				editorContent?.content?.forEach((node) => {
					node?.content?.forEach((content) => {
						if (content.type === 'mention') {
							if (mentionCounts.has(content.attrs.id)) {
								mentionCounts.set(content.attrs.id, mentionCounts.get(content.attrs.id) + 1);
							} else {
								mentionCounts.set(content.attrs.id, 1);
							}
						}
					});
				});

				mentions.forEach((mention) => {
					if (mentionCounts.get(mention.id) > 1) {
						duplicateMentions.add(mention.id);
					}
				});
			}
			return duplicateMentions;
		};

		getDuplicateMentions(mentionsToDisplay)
			.then((duplicateMentions) => {
				if (duplicateMentions.size > 0) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'created',
							actionSubject: 'mention',
							attributes: {
								isDuplicate: true,
							},
						},
					}).fire();
				}
			})
			.catch((e) => {
				setError(e);
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionsToDisplay]);

	const mentionsFlagStartTime = useRef<number | null>(null);
	const tasksFlagStartTime = useRef<number | null>(null);

	// `showCustomFlag` for confirmation flags without CTA
	const showCustomFlag = useCallback(
		({
			title,
			description,
			userPhoto,
			autoDismiss = true,
			id,
			handleOnClose,
		}: ShowCustomFlagProps) => {
			void flags.showCustomFlag({
				id: id ?? undefined,
				title,
				description,
				isAutoDismiss: autoDismiss,
				customIcon: userPhoto ? (
					<Avatar src={userPhoto} label="mention" />
				) : (
					<MentionIcon spacing="spacious" label="mention" />
				),
				onClose: handleOnClose,
			});
		},
		[flags],
	);

	const showDontNotifyFailedFlag = useCallback(() => {
		void flags.showCustomFlag({
			title: formatMessage(mentionsMessages.dontNotifyFailureTitle),
			description: formatMessage(mentionsMessages.dontNotifyFailureDescription),
			customIcon: <StatusErrorIcon spacing="spacious" label="error" />,
			isAutoDismiss: true,
		});
	}, [flags, formatMessage]);

	const {
		sendMentionNotification,
		sendCancelMentionNotification,
		contentMentionNotificationError,
	} = useMentionNotificationMutation({
		contentId,
		setError,
		showDontNotifyFailedFlag,
	});

	const {
		sendCreateInlineTaskNotifications,
		cancelInlineTaskNotifications,
		createTaskNotificationError,
	} = useCreateInlineTaskNotificationMutation({
		contentId,
		setError,
		showDontNotifyFailedFlag,
	});

	// `showCustomFlagWithAction` for:
	// - unrestricted users with "do not notify" CTA
	// - share now for restricted users
	// - deleted mention flag
	const showCustomFlagWithActions = useCallback(
		({
			title,
			description,
			userPhoto,
			actions,
			handleOnClose,
			autoDismiss = true,
			id,
		}: ShowCustomFlagWithActionsProps) => {
			void flags.showCustomFlag({
				title,
				description,
				isAutoDismiss: autoDismiss,
				customIcon: userPhoto ? (
					<Avatar src={userPhoto} label="mention" />
				) : (
					<MentionIcon spacing="spacious" label="mention" />
				),
				onClose: handleOnClose && handleOnClose,
				actions,
				id: id ?? undefined,
			});
		},
		[flags],
	);

	const displayNoPermissionsFlag = useCallback(
		({
			displayNames,
			flagFields,
			displayName,
			somebodyElse,
			flagId,
			restrictedLevel = 'page',
			hasAction = false,
			userPhoto,
		}: DisplayNoPermissionsFlagParams) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'triggered',
					actionSubject: 'mentionNoAccessFlag',
					source: 'page',
					attributes: {
						restrictedLevel,
						isLivePage,
					},
				},
			}).fire();

			const hasPageRestrictionsAndCanEdit = restrictedLevel === 'page' && canEditPageRestrictions;
			const showFlagFn = hasAction ? showCustomFlagWithActions : showCustomFlag;
			if (displayNames.size === 1) {
				showFlagFn({
					...flagFields,
					title: hasPageRestrictionsAndCanEdit
						? formatMessage(mentionsMessages.mentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(mentionsMessages.mentionFlagNoSpaceOrParentPermissionsTitle, {
								displayName,
							}),
					id: flagId,
					userPhoto,
				});
			} else if (displayNames.size === 2) {
				showFlagFn({
					...flagFields,
					title: hasPageRestrictionsAndCanEdit
						? formatMessage(mentionsMessages.mentionFlagTwoNoPermissionsTitle, {
								displayName,
								somebodyElse,
							})
						: formatMessage(mentionsMessages.mentionFlagTwoNoSpaceOrParentPermissionsTitle, {
								displayName,
								somebodyElse,
							}),
					id: flagId,
				});
			} else {
				//3 or more mentioned users
				showFlagFn({
					...flagFields,
					title: hasPageRestrictionsAndCanEdit
						? formatMessage(mentionsMessages.mentionFlagMultipleNoPermissionsTitle, {
								displayName,
								somebodyElse,
								numOthers: displayNames.size - 2,
							})
						: formatMessage(mentionsMessages.mentionFlagMultipleNoSpaceOrParentPermissionsTitle, {
								displayName,
								somebodyElse,
								numOthers: displayNames.size - 2,
							}),
					id: flagId,
				});
			}
		},
		[
			createAnalyticsEvent,
			formatMessage,
			showCustomFlag,
			showCustomFlagWithActions,
			isLivePage,
			canEditPageRestrictions,
		],
	);

	const getNotificationPayload = (
		notifyAction: NotificationAction,
		mentions: MentionType[],
		type: 'standard' | 'task',
	) => {
		const mentionsWithNotificationAction: MentionNotificationType[] = [];
		const tasksWithNotificationAction: UpdatedTaskItem[] = [];
		mentions.forEach((mention) => {
			if (type === 'standard') {
				mentionsWithNotificationAction.push({
					mentionedUserAccountId: mention.id,
					mentionLocalId: mention.localId,
					notificationAction: notifyAction,
				});
			} else if (type === 'task') {
				tasksWithNotificationAction.push({
					notificationAction: notifyAction,
					operation: Operation.ASSIGNED,
					recipientAccountId: mention.id,
					recipientMentionLocalId: mention.localId,
					taskId: mention.taskLocalId ?? '',
				});
			}
		});
		return { mentionsWithNotificationAction, tasksWithNotificationAction };
	};

	const sendNotificationFn = (
		mentions: MentionType[],
		type: 'task' | 'standard',
		closeFlagFn?: () => void,
	) => {
		const { mentionsWithNotificationAction, tasksWithNotificationAction } = getNotificationPayload(
			NotificationAction.NOTIFY,
			mentions,
			type,
		);
		if (type === 'standard') {
			sendMentionNotification({ mentions: mentionsWithNotificationAction });
		} else if (type === 'task') {
			sendCreateInlineTaskNotifications({ tasks: tasksWithNotificationAction });
		}
		closeFlagFn?.();
	};

	const showUnrestrictedPageAccessFlag = useCallback(
		({
			type,
			mentions,
			userPhoto,
		}: {
			type: 'standard' | 'task';
			mentions: MentionType[];
			userPhoto?: string;
		}) => {
			const analyticsPayload = mentions.map((mention) => {
				if (type === 'standard') {
					return {
						mentionedUserId: mention.id,
						mentionId: mention.localId,
					};
				} else if (type === 'task') {
					return {
						taskId: mention.taskLocalId,
						mentionId: mention.localId,
						mentionedUserId: mention.id,
					};
				}
			});

			let addedMentionFlagId = uuid();
			const handleFlag = (
				currentFlagId: string,
				setCurrentFlagId: React.Dispatch<React.SetStateAction<string>>,
				flagStartTime: React.MutableRefObject<number | null>,
			) => {
				if (currentFlagId) {
					addedMentionFlagId = currentFlagId;
				} else {
					setCurrentFlagId(addedMentionFlagId);
					flagStartTime.current = Date.now();
				}
			};
			if (type === 'standard') {
				handleFlag(currentAddedMentionFlagId, setCurrentAddedMentionFlagId, mentionsFlagStartTime);
			} else if (type === 'task') {
				handleFlag(currentAddedTaskFlagId, setCurrentAddedTaskFlagId, tasksFlagStartTime);
			}
			const { displayNames, displayName, somebodyElse } = getDisplayNames(mentions);

			const closeFlag = () => {
				void flags.hideFlag(addedMentionFlagId);
				if (type === 'standard') {
					setCurrentAddedMentionFlagId('');
					void setUnrestrictedMentions([]);
					mentionsFlagStartTime.current = null;
				} else if (type === 'task') {
					setCurrentAddedTaskFlagId('');
					void setUnrestrictedTasks([]);
					tasksFlagStartTime.current = null;
				}
			};

			const sendDontNotifyFn = () => {
				const { mentionsWithNotificationAction, tasksWithNotificationAction } =
					getNotificationPayload(NotificationAction.DONT_NOTIFY, mentions, type);
				if (type === 'standard') {
					void sendCancelMentionNotification({
						mentions: mentionsWithNotificationAction,
					});
					clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				} else if (type === 'task') {
					cancelInlineTaskNotifications({
						tasks: tasksWithNotificationAction,
					});
					clearTimeout(addedUnrestrictedTaskFlagTimeout.current);
				}
			};

			const handleClose = () => {
				//when "X" is clicked, or user acknowledges the flag
				const elapsedTimeInSeconds =
					type === 'standard'
						? getElapsedTime(mentionsFlagStartTime)
						: getElapsedTime(tasksFlagStartTime);

				closeFlag();

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'mentionsNotifications',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId:
							type === 'standard'
								? 'dismissMentionsNotifications'
								: 'dismissTaskMentionsNotifications',
						attributes: {
							contentId,
							elapsedTimeInSeconds,
							mentions: analyticsPayload,
						},
					},
				}).fire();
			};

			//When there is a flag currently open when a new mention is added, we want to reset the timer for the flag to be open, i.e. persist the flag to be open for another 8 seconds
			if (type === 'standard') {
				clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				addedUnrestrictedMentionFlagTimeout.current = setTimeout(() => {
					sendNotificationFn(mentions, type, closeFlag);
				}, 8000);
			} else if (type === 'task') {
				clearTimeout(addedUnrestrictedTaskFlagTimeout.current);
				addedUnrestrictedTaskFlagTimeout.current = setTimeout(() => {
					sendNotificationFn(mentions, type, closeFlag);
				}, 8000);
			}

			const sharedFlagFields = {
				description:
					type === 'standard'
						? formatMessage(mentionsMessages.mentionFlagDescription)
						: formatMessage(mentionsMessages.taskMentionFlagDescription),
				actions: [
					{
						content: formatMessage(mentionsMessages.mentionFlagAcknowledgeButton),
						onClick: handleClose,
					},
					{
						content: formatMessage(mentionsMessages.mentionFlagDoNotNotifyButton),
						onClick: () => {
							//On clicking "don't notify"
							const elapsedTimeInSeconds =
								type === 'standard'
									? getElapsedTime(mentionsFlagStartTime)
									: getElapsedTime(tasksFlagStartTime);

							sendDontNotifyFn();
							closeFlag();
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									source: 'mentionsNotifications',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: type === 'standard' ? 'doNotNotify' : 'doNotAssign',
									attributes: {
										contentId,
										mentions: analyticsPayload,
										elapsedTimeInSeconds,
									},
								},
							}).fire();
						},
					},
				],
				handleOnClose: handleClose,
				autoDismiss: false,
			};

			if (displayNames.size === 1) {
				showCustomFlagWithActions({
					...sharedFlagFields,
					title: formatMessage(mentionsMessages.mentionFlagTitle, {
						displayName,
					}),
					id: addedMentionFlagId,
					userPhoto,
				});
			} else if (displayNames.size === 2) {
				showCustomFlagWithActions({
					...sharedFlagFields,
					title: formatMessage(mentionsMessages.mentionFlagTitleTwoMentions, {
						displayName,
						somebodyElse,
					}),
					id: addedMentionFlagId,
				});
			} else {
				//3 or more mentioned users
				showCustomFlagWithActions({
					...sharedFlagFields,
					title: formatMessage(mentionsMessages.mentionFlagTitleMultipleMentions, {
						displayName,
						somebodyElse,
						numOthers: displayNames.size - 2,
					}),
					id: addedMentionFlagId,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			createAnalyticsEvent,
			currentAddedMentionFlagId,
			flags,
			formatMessage,
			sendCancelMentionNotification,
			sendMentionNotification,
			setUnrestrictedMentions,
			showCustomFlagWithActions,
			currentAddedTaskFlagId,
			sendCreateInlineTaskNotifications,
			setUnrestrictedTasks,
			cancelInlineTaskNotifications,
			mentionsFlagStartTime,
			tasksFlagStartTime,
		],
	);

	const showPageRestrictionsFlag = useCallback(
		({ type, mentions }: { type: 'standard' | 'task'; mentions: MentionType[] }) => {
			let addedMentionFlagId = uuid();
			//If a flag ID is already in local state, that means the respective flag is currently open. If not, we generate a uuid to set on the flag which is about to be opened. Having a uuid on the flag allows us to update the content without closing the flag
			if (type === 'standard' && currentPageRestrictionMentionFlagId) {
				addedMentionFlagId = currentPageRestrictionMentionFlagId;
			} else if (type === 'task' && currentPageRestrictionTaskFlagId) {
				addedMentionFlagId = currentPageRestrictionTaskFlagId;
			}
			if (type === 'standard' && !currentPageRestrictionMentionFlagId) {
				setCurrentPageRestrictionMentionFlagId(addedMentionFlagId);
			}
			if (type === 'task' && !currentPageRestrictionTaskFlagId) {
				setCurrentPageRestrictionTaskFlagId(addedMentionFlagId);
			}

			const { displayNames, displayName, somebodyElse } = getDisplayNames(mentions);

			const closeFlag = () => {
				void flags.hideFlag(addedMentionFlagId);
				if (type === 'standard') {
					clearTimeout(pageRestrictedMentionFlagTimeout.current);
					setCurrentPageRestrictionMentionFlagId('');
					void setPageRestrictedMentions([]);
				} else if (type === 'task') {
					clearTimeout(pageRestrictedTaskFlagTimeout.current);
					setCurrentPageRestrictionTaskFlagId('');
					void setPageRestrictedTasks([]);
				}
			};

			//When there is a flag currently open when a new mention is added, we want to reset the timer for the flag to be open, i.e. persist the flag to be open for another 8 seconds
			if (type === 'standard') {
				clearTimeout(pageRestrictedMentionFlagTimeout.current);
				pageRestrictedMentionFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			} else if (type === 'task') {
				clearTimeout(pageRestrictedTaskFlagTimeout.current);
				pageRestrictedTaskFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			}

			const flagActions = [
				{
					content: formatMessage(mentionsMessages.mentionFlagShareNowButton),
					onClick: () => {
						//On clicking "share now" - Update page permissions
						closeFlag();

						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								source: 'mentionNoAccessFlag',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'shareNowButton',
								attributes: {
									mentionType: type,
									isLivePage,
								},
							},
						}).fire();

						updateUsersContentPermissionMutation({
							variables: {
								contentId,
								input: mentions?.map((user) => ({
									contentRole: ContentRole.VIEWER,
									principalId: user.id,
									confluencePrincipalType: ConfluencePrincipalType.USER,
								})),
							},
						})
							.then(() => {
								// Only send immediate notification for Live Pages
								if (isLivePage) {
									sendNotificationFn(mentions, type, closeFlag);
								}
							})
							.catch((e) => {
								setError(e);
								showCustomFlag({
									title: formatMessage(mentionsMessages.mentionFlagShareFailedTitle),
									description: formatMessage(mentionsMessages.mentionFlagShareFailedDescription),
								});
							});
					},
				},
				{
					content: formatMessage(mentionsMessages.mentionFlagDontShareButton),
					onClick: () => {
						closeFlag();

						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								source: 'mentionNoAccessFlag',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'dontShareButton',
								attributes: {
									mentionType: type,
									isLivePage,
								},
							},
						}).fire();
					},
				},
			];

			const sharedFlagFields = {
				description:
					type === 'standard'
						? formatMessage(mentionsMessages.mentionFlagNoPermissionsDescription)
						: formatMessage(mentionsMessages.taskMentionFlagNoPermissionsDescription),
				actions: canEditPageRestrictions ? flagActions : undefined,
				handleOnClose: () => {
					closeFlag();

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionNoAccessFlag',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'dismissButton',
							attributes: {
								mentionType: type,
								restrictedLevel: 'page',
								isLivePage,
							},
						},
					}).fire();
				},
				autoDismiss: false,
			};

			displayNoPermissionsFlag({
				displayNames,
				flagFields: { ...sharedFlagFields, title: '' },
				displayName,
				somebodyElse,
				flagId: addedMentionFlagId,
				restrictedLevel: 'page',
				hasAction: true,
				userPhoto: mentions[0]?.userPhoto,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			canEditPageRestrictions,
			contentId,
			createAnalyticsEvent,
			currentPageRestrictionMentionFlagId,
			currentPageRestrictionTaskFlagId,
			displayNoPermissionsFlag,
			flags,
			formatMessage,
			setPageRestrictedMentions,
			setPageRestrictedTasks,
			showCustomFlag,
			updateUsersContentPermissionMutation,
		],
	);

	const showParentSpaceRestrictedFlag = useCallback(
		({ type, mentions }: { type: 'standard' | 'task'; mentions: MentionType[] }) => {
			let addedMentionFlagId = uuid();
			//If a flag ID is already in local state, that means the respective flag is currently open. If not, we generate a uuid to set on the flag which is about to be opened. Having a uuid on the flag allows us to update the content without closing the flag
			if (type === 'standard' && currentSpaceOrParentRestrictionMentionFlagId) {
				addedMentionFlagId = currentSpaceOrParentRestrictionMentionFlagId;
			} else if (type === 'task' && currentSpaceOrParentRestrictionTaskFlagId) {
				addedMentionFlagId = currentSpaceOrParentRestrictionTaskFlagId;
			}
			if (type === 'standard' && !currentSpaceOrParentRestrictionMentionFlagId) {
				setCurrentSpaceOrParentRestrictionMentionFlagId(addedMentionFlagId);
			}
			if (type === 'task' && !currentSpaceOrParentRestrictionTaskFlagId) {
				setCurrentSpaceOrParentRestrictionTaskFlagId(addedMentionFlagId);
			}

			const { displayNames, displayName, somebodyElse } = getDisplayNames(mentions);

			const closeFlag = () => {
				void flags.hideFlag(addedMentionFlagId);
				if (type === 'standard') {
					clearTimeout(spaceParentRestrictedMentionFlagTimeout.current);
					setCurrentSpaceOrParentRestrictionMentionFlagId('');
					void setParentSpaceRestrictedMentions([]);
				} else if (type === 'task') {
					clearTimeout(spaceParentRestrictedTaskFlagTimeout.current);
					setCurrentSpaceOrParentRestrictionTaskFlagId('');
					void setParentSpaceRestrictedTasks([]);
				}
			};

			//When there is a flag currently open when a new mention is added, we want to reset the timer for the flag to be open, i.e. persist the flag to be open for another 8 seconds
			if (type === 'standard') {
				clearTimeout(spaceParentRestrictedMentionFlagTimeout.current);
				spaceParentRestrictedMentionFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			} else if (type === 'task') {
				clearTimeout(spaceParentRestrictedTaskFlagTimeout.current);
				spaceParentRestrictedTaskFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			}

			const sharedFlagFields = {
				description:
					type === 'standard'
						? formatMessage(mentionsMessages.mentionFlagNoSpaceOrParentPermissionsDescription)
						: formatMessage(
								mentionsMessages.taskMentionFlagNoPermissionsParentSpaceRestrictionsDescription,
							),
				handleOnClose: () => {
					closeFlag();

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionNoAccessFlag',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'dismissButton',
							attributes: {
								mentionType: type,
								restrictedLevel: 'parent',
								isLivePage,
							},
						},
					}).fire();
				},
				autoDismiss: false,
			};

			displayNoPermissionsFlag({
				displayNames,
				flagFields: {
					...sharedFlagFields,
					title: '',
					actionContent: '',
					actionOnClick: () => {},
				},
				displayName,
				somebodyElse,
				restrictedLevel: 'parent',
				flagId: addedMentionFlagId,
				userPhoto: mentions[0]?.userPhoto,
			});
		},
		[
			createAnalyticsEvent,
			currentSpaceOrParentRestrictionMentionFlagId,
			currentSpaceOrParentRestrictionTaskFlagId,
			formatMessage,
			displayNoPermissionsFlag,
			flags,
			setParentSpaceRestrictedMentions,
			setParentSpaceRestrictedTasks,
			isLivePage,
		],
	);

	useEffect(() => {
		//Handling deleted mentions
		if (deletedMentions.length > 0) {
			//Handles updating content of current open mention flags
			const deletedMentionsLocalIds = new Set(deletedMentions.map((mention) => mention.localId));
			//Filter out deleted mentions from current open general mention flag
			const remainingUnrestrictedStandardMentions = unrestrictedMentions.filter(
				(unrestrictedMention) => !deletedMentionsLocalIds.has(unrestrictedMention.localId),
			);
			const remainingParentSpaceRestrictedStandardMentions = parentSpaceRestrictedMentions.filter(
				(parentSpaceRestrictedMention) =>
					!deletedMentionsLocalIds.has(parentSpaceRestrictedMention.localId),
			);
			const remainingPageRestrictedStandardMentions = pageRestrictedMentions.filter(
				(pageRestrictedMention) => !deletedMentionsLocalIds.has(pageRestrictedMention.localId),
			);

			//Close general mention flag if none remain
			if (remainingUnrestrictedStandardMentions?.length === 0) {
				clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentAddedMentionFlagId);
				setCurrentAddedMentionFlagId('');
			}
			if (remainingParentSpaceRestrictedStandardMentions?.length === 0) {
				clearTimeout(spaceParentRestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentSpaceOrParentRestrictionMentionFlagId);
				setCurrentSpaceOrParentRestrictionMentionFlagId('');
			}
			if (remainingPageRestrictedStandardMentions?.length === 0) {
				clearTimeout(pageRestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentPageRestrictionMentionFlagId);
				setCurrentPageRestrictionMentionFlagId('');
			}
			void setUnrestrictedMentions(remainingUnrestrictedStandardMentions);
			void setParentSpaceRestrictedMentions(remainingParentSpaceRestrictedStandardMentions);
			void setPageRestrictedMentions(remainingPageRestrictedStandardMentions);
			void setDeletedMentions([]);
		}
		if (deletedTasks.length > 0) {
			//Handles updating content of current open task flags
			const deletedTasksLocalIds = new Set(deletedTasks.map((mention) => mention.localId));
			//Filter out deleted mentions from current open task mention flag
			const remainingUnrestrictedTasks = unrestrictedTasks.filter(
				(unrestrictedTask) => !deletedTasksLocalIds.has(unrestrictedTask.localId),
			);
			const remainingParentSpaceRestrictedTasks = parentSpaceRestrictedTasks.filter(
				(parentSpaceRestrictedTask) => !deletedTasksLocalIds.has(parentSpaceRestrictedTask.localId),
			);
			const remainingPageRestrictedTasks = pageRestrictedTasks.filter(
				(pageRestrictedTask) => !deletedTasksLocalIds.has(pageRestrictedTask.localId),
			);

			//Close task mention flag if none remain
			if (remainingUnrestrictedTasks?.length === 0) {
				clearTimeout(addedUnrestrictedTaskFlagTimeout.current);
				void flags.hideFlag(currentAddedTaskFlagId);
				setCurrentAddedTaskFlagId('');
			}
			if (remainingParentSpaceRestrictedTasks?.length === 0) {
				clearTimeout(spaceParentRestrictedTaskFlagTimeout.current);
				void flags.hideFlag(currentSpaceOrParentRestrictionTaskFlagId);
				setCurrentSpaceOrParentRestrictionTaskFlagId('');
			}
			if (remainingPageRestrictedTasks?.length === 0) {
				clearTimeout(pageRestrictedTaskFlagTimeout.current);
				void flags.hideFlag(currentPageRestrictionTaskFlagId);
				setCurrentPageRestrictionTaskFlagId('');
			}
			void setUnrestrictedTasks(remainingUnrestrictedTasks);
			void setParentSpaceRestrictedTasks(remainingParentSpaceRestrictedTasks);
			void setPageRestrictedTasks(remainingPageRestrictedTasks);
			void setDeletedTasks([]);
		}
		//leaving addedMentions out of the dependency array otherwise the deleted flag will be displayed twice
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		setDeletedMentions,
		flags,
		showCustomFlagWithActions,
		formatMessage,
		setAddedMentions,
		deletedMentions,
		setDeletedTasks,
		deletedTasks,
	]);

	const checkPastedMentions = (mentions: MentionType[]) => {
		return mentions.some((mention) => mention.method === 'pasted');
	};

	useEffect(() => {
		if (!updatedUsersContentPermissionsLoading && unrestrictedMentions?.length) {
			if (isLivePage) {
				if (checkPastedMentions(unrestrictedMentions)) {
					// When mention is pasted, show flag
					showUnrestrictedPageAccessFlag({
						type: 'standard',
						mentions: unrestrictedMentions,
						userPhoto: unrestrictedMentions[0]?.userPhoto,
					});
				} else {
					// When mention is typed, only send notification
					sendNotificationFn(unrestrictedMentions, 'standard');
					void setUnrestrictedMentions([]);
				}
			} else {
				void setUnrestrictedMentions([]);
			}
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [unrestrictedMentions, updatedUsersContentPermissionsLoading, isLivePage]);

	useEffect(() => {
		if (!updatedUsersContentPermissionsLoading && unrestrictedTasks?.length) {
			if (isLivePage) {
				if (checkPastedMentions(unrestrictedTasks)) {
					// When mention is pasted, show flag
					showUnrestrictedPageAccessFlag({
						type: 'task',
						mentions: unrestrictedTasks,
						userPhoto: unrestrictedTasks[0]?.userPhoto,
					});
				} else {
					// When mention is typed, only send notification
					sendNotificationFn(unrestrictedTasks, 'task');
					void setUnrestrictedTasks([]);
				}
			} else {
				void setUnrestrictedTasks([]);
			}
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [unrestrictedTasks, updatedUsersContentPermissionsLoading, isLivePage]);

	useEffect(() => {
		if (!updatedUsersContentPermissionsLoading && parentSpaceRestrictedMentions?.length) {
			showParentSpaceRestrictedFlag({ type: 'standard', mentions: parentSpaceRestrictedMentions });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, parentSpaceRestrictedMentions, updatedUsersContentPermissionsLoading]);

	useEffect(() => {
		if (!updatedUsersContentPermissionsLoading && parentSpaceRestrictedTasks?.length) {
			showParentSpaceRestrictedFlag({ type: 'task', mentions: parentSpaceRestrictedTasks });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, parentSpaceRestrictedTasks, updatedUsersContentPermissionsLoading]);

	useEffect(() => {
		if (!updatedUsersContentPermissionsLoading && pageRestrictedMentions?.length) {
			showPageRestrictionsFlag({ type: 'standard', mentions: pageRestrictedMentions });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		canEditPageRestrictions,
		flags,
		pageRestrictedMentions,
		updatedUsersContentPermissionsLoading,
	]);

	useEffect(() => {
		if (!updatedUsersContentPermissionsLoading && pageRestrictedTasks?.length) {
			showPageRestrictionsFlag({ type: 'task', mentions: pageRestrictedTasks });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canEditPageRestrictions, flags, pageRestrictedTasks, updatedUsersContentPermissionsLoading]);

	useEffect(() => {
		if (mentionsAdded.currentUser.length > 0) {
			if (isLivePage) {
				const { mentionsWithNotificationAction } = getNotificationPayload(
					NotificationAction.DONT_NOTIFY,
					mentionsAdded.currentUser,
					'standard',
				);
				void sendCancelMentionNotification({
					mentions: mentionsWithNotificationAction,
				});
			}
			void setMentionsAdded({ currentUser: [] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionsAdded.currentUser]);

	useEffect(() => {
		if (tasksAdded.currentUser.length > 0) {
			if (isLivePage) {
				const { tasksWithNotificationAction } = getNotificationPayload(
					NotificationAction.DONT_NOTIFY,
					tasksAdded.currentUser,
					'task',
				);
				void cancelInlineTaskNotifications({
					tasks: tasksWithNotificationAction,
				});
			}
			void setTasksAdded({ currentUser: [] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tasksAdded.currentUser]);

	return (
		<>
			{error && <ErrorDisplay error={error} />}
			{createTaskNotificationError && <ErrorDisplay error={createTaskNotificationError} />}
			{contentMentionNotificationError && <ErrorDisplay error={contentMentionNotificationError} />}
			{updatedUsersContentPermissionsError && (
				<ErrorDisplay error={updatedUsersContentPermissionsError} />
			)}
		</>
	);
};

export const MentionsNotificationsWrapper = ({
	children,
	flags,
	contentId,
	isLivePage,
	isMentionsNotificationsEnabled,
	editorActions,
}: MentionsNotificationsProps) => {
	return (
		<>
			<ErrorBoundary attribution={Attribution.COMMENTS}>
				<MentionsNotificationsComponent
					flags={flags}
					contentId={contentId}
					isLivePage={isLivePage}
					isMentionsNotificationsEnabled={isMentionsNotificationsEnabled}
					editorActions={editorActions}
				/>
			</ErrorBoundary>
			{children}
		</>
	);
};
